import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { EmptyState, StatusBadge } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export function AssetsTab({ assets, employees, onRefresh }) {
  const [assetModal, setAssetModal] = useState(false);
  const [asset, setAsset] = useState({ employee_id: "", type: "laptop", name: "", serial: "" });

  const issueAsset = async () => {
    try {
      await api.post("/employer/assets", asset);
      toast.success("Asset issued — employee will be asked to acknowledge");
      setAssetModal(false);
      setAsset({ employee_id: "", type: "laptop", name: "", serial: "" });
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const returnAsset = async (id) => {
    try {
      await api.post(`/employer/assets/${id}/return`);
      toast.success("Asset return recorded");
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <TabsContent value="assets">
      <div className="flex justify-end mb-4">
        <Button onClick={() => setAssetModal(true)} disabled={employees.length === 0} className="rounded-full bg-[#0B1B3D]" data-testid="issue-asset-btn">Issue Asset</Button>
      </div>
      {assets.length === 0 ? (
        <EmptyState title="No assets issued" description="Track laptops, ID cards, access cards and equipment issued to employees." testId="assets-empty" />
      ) : (
        <div className="space-y-3" data-testid="assets-list">
          {assets.map((a) => (
            <div key={a.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`asset-${a.id}`}>
              <div>
                <p className="font-semibold text-sm text-[#0B1B3D]">{a.name} <span className="text-xs text-muted-foreground capitalize">({a.type.replace(/_/g, " ")})</span></p>
                <p className="text-xs text-muted-foreground mt-1">{a.employee_name} {a.serial && `· S/N ${a.serial}`} · {fmtDate(a.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={a.status} />
                {["issued", "acknowledged"].includes(a.status) && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => returnAsset(a.id)} data-testid={`asset-return-${a.id}`}>Mark Returned</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={assetModal} onOpenChange={setAssetModal}>
        <DialogContent aria-describedby={undefined} data-testid="asset-dialog">
          <DialogHeader><DialogTitle>Issue asset</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={asset.employee_id} onValueChange={(v) => setAsset({ ...asset, employee_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="asset-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={asset.type} onValueChange={(v) => setAsset({ ...asset, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="asset-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["laptop", "mobile", "sim", "id_card", "access_card", "equipment", "vehicle", "other"].map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Serial no.</Label><Input value={asset.serial} onChange={(e) => setAsset({ ...asset, serial: e.target.value })} className="mt-1.5" data-testid="asset-serial" /></div>
            </div>
            <div><Label>Asset name</Label><Input value={asset.name} onChange={(e) => setAsset({ ...asset, name: e.target.value })} placeholder="e.g. MacBook Pro 14" className="mt-1.5" data-testid="asset-name" /></div>
            <Button onClick={issueAsset} disabled={!asset.employee_id || !asset.name} className="w-full rounded-full bg-[#0B1B3D]" data-testid="asset-submit-btn">Issue Asset</Button>
          </div>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
